import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../api';

export const sourceTime = value => value ? new Date(/[Z+-]\d*:?\d*$/.test(value) ? value : `${value}Z`).toLocaleString('ru-RU', { timeZone: 'Europe/Moscow' }) : '—';
export const errorText = e => typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Не удалось выполнить операцию. Повторите попытку.';

export default function PersonalDashboard({panelId}) {
  const [state, setState] = useState(null);
  const [error, setError] = useState('');
  const [save, setSave] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [job, setJob] = useState(null);
  const frame = useRef(null);
  const revisions = useRef({});
  const writes = useRef(Promise.resolve());
  const stopped = useRef(false);
  const epoch = useRef(0);
  const load = useCallback(async () => {
    const requestEpoch = epoch.current;
    try {
      const { data } = await api.get('/tenant/manager-dashboard/current/view', {params: {panel_id: panelId}});
      if (stopped.current || requestEpoch !== epoch.current) return;
      revisions.current = Object.fromEntries(Object.entries(data.settings).map(([k, v]) => [k, v.revision]));
      setState(data); setJob(data.source.current_job || null); setError('');
    } catch (e) { if (!stopped.current && requestEpoch === epoch.current) { setState(null); setError(errorText(e)); } }
  }, [panelId]);
  useEffect(() => {
    const generation = epoch.current + 1;
    epoch.current = generation; stopped.current = false; setState(null); setJob(null); load();
    return () => { stopped.current = true; epoch.current = generation + 1; };
  }, [load]);
  const refresh = useCallback(async (mode = 'incremental') => {
    setRefreshing(true); setError('');
    try {
      await writes.current;
      const {data} = await api.post('/tenant/manager-dashboard/current/refresh', null, {params:{panel_id:panelId,mode}});
      if (!stopped.current) { setJob(data.job); setSave('Обновление поставлено в очередь. Прежний снимок остаётся доступен до успешной сверки.'); }
    }
    catch (e) { setError(errorText(e)); }
    finally { setRefreshing(false); }
  }, [panelId]);
  const running = ['queued','running'].includes(job?.status);
  useEffect(() => {
    if (!running) return;
    let active = true;
    const timer = setInterval(async () => {
      try {
        const {data} = await api.get(`/tenant/manager-dashboard/current/jobs/${job.id}`, {params:{panel_id:panelId}});
        if (!active || stopped.current) return;
        setJob(data);
        if (data.status === 'success') { setSave('Серверное обновление завершено'); await load(); }
        if (data.status === 'failed') setError(data.error || 'Обновление не завершено; прежний снимок сохранён.');
      } catch (e) { if (active) setError(errorText(e)); }
    }, 2000);
    return () => { active = false; clearInterval(timer); };
  }, [running,job?.id,panelId,load]);
  const importSettings = async event => {
    const file = event.target.files?.[0];
    if (!file || !state?.can_edit) return;
    try {
      const storage = JSON.parse(await file.text());
      await writes.current;
      await api.post('/tenant/manager-dashboard/current/import-settings', {storage, revisions:revisions.current}, {params:{panel_id:panelId}});
      await load(); setSave('Настройки прежней панели перенесены на сервер');
    } catch(e) { setError(e instanceof SyntaxError ? 'Файл не является JSON-экспортом настроек.' : errorText(e)); }
    finally { event.target.value=''; }
  };
  useEffect(() => {
    const message = event => {
      if (event.source !== frame.current?.contentWindow || event.data?.channel !== state?.bridge_key) return;
      if (event.data.type === 'refresh') { refresh(); return; }
      if (event.data.type === 'import_requested') { setError('Импорт снимка выполняется администратором для выбранной панели.'); return; }
      if (event.data.type !== 'setting' || !state.can_edit || stopped.current) return;
      const { namespace, payload } = event.data;
      setSave('Сохраняем на сервере…');
      writes.current = writes.current.then(async () => {
        if (stopped.current) return;
        try {
          const { data } = await api.put('/tenant/manager-dashboard/current/settings', {
            namespace, payload, revision: revisions.current[namespace] || 0,
          }, {params:{panel_id:panelId}});
          revisions.current[namespace] = data.revision;
          setSave('Сохранено на сервере');
        } catch (e) {
          stopped.current = true;
          setSave('Изменения не сохранены');
          setError(`${errorText(e)} Перечитайте панель перед дальнейшими изменениями.`);
        }
      });
    };
    window.addEventListener('message', message);
    return () => window.removeEventListener('message', message);
  }, [state, refresh, panelId]);
  return <section aria-label="Персональная панель">
    <div className="workspace-heading"><div><h1>{state?.panel.name || 'Дашборд Менеджеры'}</h1>
      <p>Данные и настройки назначенной группы клиентов</p></div>
      <div className="workspace-actions"><button onClick={() => refresh()} disabled={!state?.can_edit || refreshing || running}>{running ? 'Обновление выполняется…' : refreshing ? 'Запускаем…' : 'Обновить данные'}</button>
      {state?.can_rebuild && <button onClick={() => refresh('full')} disabled={refreshing || running}>Полный пересбор</button>}</div></div>
    {error && <div role="alert" className="workspace-alert">{error}<button onClick={load}>Перечитать панель</button></div>}
    {state && <div className="workspace-source">
      <span>Данные на: <strong>{sourceTime(state.snapshot?.source_fact_at)}</strong></span>
      <span>Публикация: {sourceTime(state.snapshot?.published_at)}</span>
      <span>Последнее успешное обновление: {sourceTime(state.source.last_successful_update_at)}</span>
      <span>{state.source.token_configured ? 'Токен настроен' : 'Источник не подключён'}</span>
      {!state.can_edit && <span>Только просмотр</span>}
      {state.snapshot?.origin === 'import' && <span>Импортированный снимок</span>}
      <span role="status">{save}</span>
    </div>}
    {job && <p role="status">Обновление: {({queued:'Ожидает запуска',running:'Выполняется',success:'Завершено',failed:'Ошибка'})[job.status] || job.status}</p>}
    {job?.status === 'failed' && <p role="alert">{job.error || 'Проверенный снимок не изменён'}</p>}
    {state?.source.warnings?.map((warning,index) => <p key={index} className="workspace-alert">{warning.message}</p>)}
    {state?.html && <details className="workspace-settings-import"><summary>Состав клиентов в планах</summary>
      <p>История планов хранит зафиксированный состав группы. Карточки выбранного месяца и темп выполнения используют текущих активных клиентов, как в исходной панели. После изменения состава эти показатели могут отличаться от истории.</p>
      <p>Если клиент выбыл из группы, его сохранённый исторический факт остаётся с прежним временем. Новый факт для такого клиента автоматически не подтверждается.</p>
    </details>}
    {state?.can_edit && <details className="workspace-settings-import"><summary>Перенос настроек прежней панели</summary>
      <p>Выберите JSON-экспорт из браузера с вашей прежней панелью Торлопова. Снимок HTML не содержит сохранённые в браузере заметки и правки.</p>
      <label>Файл настроек<input type="file" accept=".json,application/json" onChange={importSettings}/></label>
    </details>}
    {!state && !error && <p role="status">Загружаем панель…</p>}
    {state && !state.html && <p className="workspace-empty">Проверенный снимок этой панели ещё не импортирован.</p>}
    {state?.html && <iframe ref={frame} title="Панель группы клиентов" className="personal-panel-frame"
      sandbox="allow-scripts allow-downloads allow-forms" srcDoc={state.html} />}
  </section>;
}
